import React, { useCallback } from 'react';
import {
    Box,
    Button,
    Flex,
    IconButton,
    Text,
    useToast,
} from '@chakra-ui/react';
import Icon from '@/components/Icon';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    fullMessage: string;
    status?: string | null;
    timestampLabel?: string | null;
};

export const GradeSyncLogErrorDetailPanel = ({
    isOpen,
    onClose,
    fullMessage,
    status,
    timestampLabel,
}: Props) => {
    const toast = useToast();

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(fullMessage);
            toast({
                title: 'Error copied to clipboard',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });
        } catch {
            toast({
                title: 'Failed to copy error',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    }, [fullMessage, toast]);

    return (
        <Box
            position="absolute"
            top={0}
            right={0}
            bottom={0}
            width="100%"
            bg="white"
            zIndex={1}
            borderLeft="1px solid"
            borderColor="gray.200"
            boxShadow="lg"
            display="flex"
            flexDirection="column"
            transform={isOpen ? 'translateX(0)' : 'translateX(100%)'}
            transition="transform 0.2s ease-in-out"
            pointerEvents={isOpen ? 'auto' : 'none'}
            aria-hidden={!isOpen}>
            <Flex
                align="center"
                justify="space-between"
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor="gray.200">
                <Box>
                    <Text fontSize="18px" fontWeight="600" color="#202020">
                        Full error details
                    </Text>
                    {(status || timestampLabel) && (
                        <Text fontSize="12px" color="gray.600" mt={1}>
                            {[status, timestampLabel].filter(Boolean).join(' · ')}
                        </Text>
                    )}
                </Box>
                <IconButton
                    aria-label="Back to grade sync logs"
                    icon={<Icon icon="chevron-left" />}
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                />
            </Flex>

            <Box
                flex="1"
                px={4}
                py={3}
                overflowY="auto"
                overflowX="auto"
                maxH="calc(70vh - 120px)">
                <Box
                    as="pre"
                    m={0}
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    fontFamily="mono"
                    fontSize="12px"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                    sx={{ overflowWrap: 'anywhere' }}>
                    {fullMessage}
                </Box>
            </Box>

            <Flex px={4} py={3} gap={3} borderTop="1px solid" borderColor="gray.200" justify="flex-end">
                <Button variant="outline" onClick={onClose} borderRadius="40px">
                    Back
                </Button>
                <Button variant="brand" onClick={handleCopy} borderRadius="40px">
                    Copy error
                </Button>
            </Flex>
        </Box>
    );
};
